import { Component, OnInit, inject, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { createUserWithEmailAndPassword, UserCredential } from 'firebase/auth';
import { User } from '@core/models/user';
import { Company } from '@core/models/company';
import { UserService } from '@core/services/user.service';
import { ThemeService } from '@core/services/theme.service';
import { PaymentService } from '@core/services/payment.service';
import { SalesNotificationService } from '@core/services/sales-notification.service';
import { FirebaseInjectionHelperService } from '@core/services/firebase-injection-helper.service';
import { AuthService } from '@core/services/auth.service';
import { CompanyService } from '@core/services/company.service';
import { companyDomainFromEmail } from '@core/utils/public-email-domains';
import { MIN_PASSWORD_LENGTH, PASSWORD_LENGTH_HINT } from '@login/shared/helpers/password-validators';
import {
  SignupMethod,
  signupMethodFromProviders,
  trackSignupConversion,
} from '@core/utils/signup-conversion';

/** Where a new company goes once it exists: the plan step (#943). */
export const PLAN_STEP_ROUTE = '/dashboard/company/subscription-plans';

/**
 * QuickSignupComponent: the one signup screen (#943).
 *
 * - **Continue with Microsoft / Google** come first. They use the same
 *   Firebase providers and first-login code as /login (the sign-in
 *   directives, `AuthService.AuthLogin`, `getOrCreateUserModel`). An SSO user
 *   needs no password and no verification email; the post-login hub brings
 *   them back here, where they are asked only for the company name (and the
 *   terms, if not yet accepted).
 * - **Email path**: name, work email, company, password (15+ characters, no
 *   composition rules) and terms. The account and the company are created
 *   together, the verification email is sent, and the user goes straight to
 *   the plan step. Verification no longer blocks: a banner reminds them, and
 *   only inviting teammates and exporting reports require it.
 *
 * New companies have no plan and no Coins until Stripe confirms payment; a
 * channel-partner signup (`?partner=`) is stamped by the API and its plan is
 * set up by the partner or Scan Ninja (#847, #943).
 */
@Component({
  selector: 'app-quick-signup',
  templateUrl: './quick-signup.component.html',
  styleUrl: './quick-signup.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class QuickSignupComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private userService = inject(UserService);
  private themeService = inject(ThemeService);
  private paymentService = inject(PaymentService);
  private salesNotificationService = inject(SalesNotificationService);
  private firebaseHelper = inject(FirebaseInjectionHelperService);
  private authService = inject(AuthService);
  private companyService = inject(CompanyService);
  private platformId = inject(PLATFORM_ID);

  quickSignupForm!: FormGroup;
  loading = false;
  serverMessage?: string;

  /**
   * `signup`: the full form. `company`: a signed-in user with no company
   * (typically just back from Microsoft or Google), asked only for what is
   * missing. `checking`: deciding which, for a signed-in visitor.
   */
  mode: 'signup' | 'company' | 'checking' = 'signup';

  /** The signed-in user in `company` mode. */
  private signedInUser: User | null = null;

  /** Shown under the password field. */
  readonly passwordHint = PASSWORD_LENGTH_HINT;

  constructor() {
    this.initializeForm();
  }

  ngOnInit(): void {
    if (this.firebaseHelper.getAuth().currentUser) {
      this.mode = 'checking';
      void this.resumeSignedInUser();
    }
  }

  /**
   * A signed-in visitor either already has a company (go on to the app) or is
   * an SSO signup that still needs one: switch to the company-only form,
   * prefilled with any name held from an earlier attempt.
   */
  private async resumeSignedInUser(): Promise<void> {
    const user = await this.authService.waitForHydratedUser(10000);
    if (!user || user.currentCompanyId || (user.companies?.length ?? 0) > 0) {
      await this.router.navigate(['/logging-in'], { replaceUrl: true });
      return;
    }
    this.signedInUser = user;
    for (const field of ['firstName', 'lastName', 'email', 'password']) {
      this.quickSignupForm.get(field)?.disable();
    }
    if (user.termsAcceptedAt) {
      this.quickSignupForm.get('agreeToTerms')?.disable();
    }
    if (user.pendingCompanyName) {
      this.quickSignupForm.get('companyName')?.setValue(user.pendingCompanyName);
    }
    this.mode = 'company';
  }

  /** True when the terms checkbox still has to be shown. */
  get needsTerms(): boolean {
    return this.quickSignupForm.get('agreeToTerms')?.enabled ?? true;
  }

  /**
   * Initialize the quick signup form with validators
   */
  private initializeForm(): void {
    this.quickSignupForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      companyName: ['', [Validators.required, Validators.minLength(2)]],
      // Length only (#943): NIST SP 800-63B-4 forbids composition rules.
      password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
      agreeToTerms: [false, [Validators.requiredTrue]],
    });
  }

  /**
   * Creates the account and the company, sends the verification email, and
   * goes straight to the plan step. In `company` mode, creates only the
   * company for the signed-in user.
   */
  async onSubmit(): Promise<void> {
    if (!this.quickSignupForm.valid) {
      this.markFormGroupTouched();
      return;
    }
    if (this.mode === 'company' && this.signedInUser) {
      await this.submitCompanyOnly(this.signedInUser);
      return;
    }

    this.loading = true;
    this.serverMessage = undefined;
    this.themeService.showSpinner();

    try {
      const formData = this.quickSignupForm.value;
      const companyName = String(formData.companyName).trim();

      // Create Firebase user
      const userCredential = await this.createAuthUser(
        formData.email,
        formData.password,
      );
      // Use the address as Firebase stored it: the users create rule compares
      // it with the token email, which Firebase normalizes.
      const email = userCredential.user.email ?? formData.email;

      // Create User model with default 'user' role. The company name is also
      // held on the user document, so that if creating the company below
      // fails, the company step can prefill it and the user never types it
      // twice (#870).
      const userData = new User({
        uid: userCredential.user.uid,
        email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: `${formData.firstName} ${formData.lastName}`,
        role: 'user', // Explicitly set role for API authorization
        emailVerified: userCredential.user.emailVerified,
        pendingCompanyName: companyName,
        termsAcceptedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        metadata: {
          createdAt: userCredential.user.metadata.creationTime || '',
          lastLoginAt: userCredential.user.metadata.lastSignInTime || '',
          lastSignInTime: userCredential.user.metadata.lastSignInTime || '',
          creationTime: userCredential.user.metadata.creationTime || '',
        },
      });

      // Save user to Firestore. create() returns rather than throws on a
      // denied write; carrying on would leave an account with no profile.
      const created = await this.userService.create(userData, userData.uid);
      if (created instanceof Error) {
        throw created;
      }

      await this.userService.recordAcquisitionAttribution(userData);

      // Notify sales now: this is the moment the lead exists. Waiting for the
      // company write meant the notification never fired, because that write
      // is always refused before verification. It runs before the verification
      // email so a failed send cannot lose the lead; it never throws.
      await this.triggerSalesNotification(
        userData,
        new Company({
          name: companyName,
          domain: this.extractDomainFromEmail(email),
          ownerId: userData.uid,
        }),
      );

      // Send the verification email. This path creates the account with
      // createUserWithEmailAndPassword and never sent one, so a quick-signup
      // user had nothing to click.
      await this.userService.sendVerificationEmail(userCredential.user);

      // Create Stripe customer for the new user (non-blocking)
      this.paymentService.createOrGetStripeCustomer().catch((err: any) => {
        console.warn('Failed to create Stripe customer during quick signup:', err);
      });

      // Verification no longer blocks (#943): create the company now and go
      // straight to the plan step. The dashboard banner reminds them to verify.
      await this.createCompanyAndContinue(userData, companyName, 'email');
    } catch (error: any) {
      console.error('Quick signup error:', error);
      this.loading = false;
      this.themeService.hideSpinner();

      if (error.code === 'auth/email-already-in-use') {
        this.serverMessage =
          'An account with this email already exists. Please sign in instead.';
      } else if (error.code === 'auth/weak-password') {
        this.serverMessage =
          'Password is too weak. Please choose a stronger password.';
      } else {
        this.serverMessage =
          error.message || 'An error occurred during signup. Please try again.';
      }
    }
  }

  /**
   * Company-only step for a signed-in user with no company: an SSO signup, or
   * an email signup resuming after its company creation failed. Records the terms if they were not accepted yet, then creates
   * the company.
   *
   * @param user The signed-in user.
   */
  private async submitCompanyOnly(user: User): Promise<void> {
    this.loading = true;
    this.serverMessage = undefined;
    this.themeService.showSpinner();
    const companyName = String(this.quickSignupForm.get('companyName')?.value ?? '').trim();
    if (!user.termsAcceptedAt) {
      const accepted = await this.userService.update(
        { termsAcceptedAt: new Date().toISOString() },
        user.uid,
      );
      if (accepted instanceof Error) {
        this.loading = false;
        this.themeService.hideSpinner();
        this.serverMessage = accepted.message || 'We could not save your details. Please try again.';
        return;
      }
    }
    // This form also resumes an email signup whose company creation failed,
    // so the method comes from the account's providers, not the form (#994).
    const providers = (this.firebaseHelper.getAuth().currentUser?.providerData ?? []).map(
      (p) => p.providerId,
    );
    await this.createCompanyAndContinue(user, companyName, signupMethodFromProviders(providers));
  }

  /**
   * Creates the company (no plan, #847), links the user as its owner and goes
   * to the plan step. The API stamps a channel partner from the signup link;
   * the plan step then shows the partner note instead of prices (#943).
   *
   * On failure the account is kept and the post-login hub resumes: it brings
   * the user back to the company-only form with the name prefilled.
   *
   * @param user The account owner.
   * @param companyName Company name as typed.
   * @param method How the account was created, for the ad conversion (#994).
   */
  private async createCompanyAndContinue(
    user: User,
    companyName: string,
    method: SignupMethod,
  ): Promise<void> {
    try {
      const company = await this.companyService.createCompanyAtomic({
        ownerId: user.uid,
        name: companyName,
        domain: this.extractDomainFromEmail(user.email),
      });
      this.companyService.company = company;

      // The signup is complete: count it for Google Ads and Microsoft Ads.
      // It fires here, once, because the plan step is not a signup-only page
      // (#994).
      if (isPlatformBrowser(this.platformId)) {
        trackSignupConversion(method);
      }

      // Non-privileged signup flags the client still owns, as the company
      // step sets them: no scanning-config detour, and the held name is done.
      const flags = await this.userService.update(
        { scanningConfigCompleted: true, intendedTier: 'premium', pendingCompanyName: null },
        user.uid,
      );
      if (flags instanceof Error) {
        console.warn('Quick signup: could not save signup flags', flags);
      }

      // The wait page and guards read AuthService's copy of the user, hydrated
      // before the company existed (#524).
      await this.authService.refreshHydratedUser();
      this.themeService.hideSpinner();
      await this.router.navigate([PLAN_STEP_ROUTE], { replaceUrl: true });
    } catch (error) {
      console.error('Quick signup: company creation failed', error);
      this.themeService.hideSpinner();
      await this.router.navigate(['/logging-in'], { replaceUrl: true });
    }
  }

  /**
   * Creates the Firebase Auth account. A method rather than an inline call so
   * specs can stub it: the Firebase SDK is an ES module, and `spyOn` cannot
   * replace its exports.
   *
   * @param email Account email.
   * @param password Account password.
   * @returns The new user's credential.
   */
  protected createAuthUser(email: string, password: string): Promise<UserCredential> {
    return createUserWithEmailAndPassword(this.firebaseHelper.getAuth(), email, password);
  }

  /**
   * Extract domain from email for company domain field
   */
  private extractDomainFromEmail(email: string): string {
    return companyDomainFromEmail(email);
  }

  /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(): void {
    Object.keys(this.quickSignupForm.controls).forEach((field) => {
      const control = this.quickSignupForm.get(field);
      control?.markAsTouched({ onlySelf: true });
    });
  }

  // Form field accessors
  get firstName() {
    return this.quickSignupForm.get('firstName');
  }

  get lastName() {
    return this.quickSignupForm.get('lastName');
  }

  get email() {
    return this.quickSignupForm.get('email');
  }

  get companyName() {
    return this.quickSignupForm.get('companyName');
  }

  get password() {
    return this.quickSignupForm.get('password');
  }

  get agreeToTerms() {
    return this.quickSignupForm.get('agreeToTerms');
  }

  // Validation helpers
  isFieldInvalid(fieldName: string): boolean {
    const field = this.quickSignupForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.quickSignupForm.get(fieldName);
    if (field?.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        return `${this.getFieldDisplayName(fieldName)} is required`;
      }
      if (field.errors['minlength']) {
        return `${this.getFieldDisplayName(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['requiredTrue']) {
        return 'You must agree to the terms and conditions';
      }
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      companyName: 'Company name',
      password: 'Password',
      agreeToTerms: 'Terms agreement',
    };
    return displayNames[fieldName] || fieldName;
  }

  /**
   * Characters still needed to reach the minimum, for the length hint. Zero
   * once the password is long enough.
   */
  get passwordCharactersNeeded(): number {
    const length = String(this.password?.value ?? '').length;
    return Math.max(0, MIN_PASSWORD_LENGTH - length);
  }

  /**
   * Trigger sales notification for new user registration
   * @param user Created user data
   * @param company Created company data
   */
  private async triggerSalesNotification(
    user: User,
    company: Company,
  ): Promise<void> {
    try {
      // Create notification payload with quick signup context
      const notificationPayload =
        this.salesNotificationService.createNotificationPayload(
          user,
          company,
          'quick-signup',
        );

      // Send notification to sales team
      await this.salesNotificationService.notifyNewUserRegistration(
        notificationPayload,
      );

    } catch (error) {
      console.error('Failed to send quick signup sales notification:', error);
      // Don't fail the signup process if notification fails
    }
  }
}
